import { useState, useEffect } from 'react';
import { apiConfig, buildApiUrl } from '../config/api';

interface WorkflowSummary {
  totalWorkflows: number;
  totalCards: number;
  completedCards: number;
  readyCards: number;
  overdueCards: number;
  overallCompletionRate: number;
}

interface Workflow {
  name: string;
  workflow_id: string;
  total_cards_count: number;
  completed_card_count: number;
  my_ready_card_count: number;
  my_overdue_card_count: number;
  completion_rate: string;
  created_at: string;
}

interface WorkflowActivity {
  workflow_card_id: string;
  workflow_name: string;
  stage: string;
  overdue: boolean;
  created_at: string;
  completed_at: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface WorkflowCategory {
  workflow_category_id: string;
  name: string;
  created_at: string;
}

interface WorkflowData {
  summary: WorkflowSummary;
  workflows: Workflow[];
  recentActivity: WorkflowActivity[];
  categories: WorkflowCategory[];
}

export default function Workflows() {
  const [workflowData, setWorkflowData] = useState<WorkflowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchWorkflowData();
  }, []);

  const fetchWorkflowData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(buildApiUrl(apiConfig.endpoints.workflows.analytics));
      
      if (!response.ok) {
        throw new Error(`Failed to fetch workflow data: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setWorkflowData(data.data);
      } else {
        setError(data.error?.message || 'Failed to load workflow data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStageColor = (stage: string, overdue: boolean) => {
    if (overdue) return 'bg-red-100 text-red-800';
    switch (stage) {
      case 'ready': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'snoozed': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Workflows</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button
                onClick={fetchWorkflowData}
                className="mt-3 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!workflowData) {
    return <div>No data available</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">PCO Workflows Analytics</h1>
        <p className="mt-2 text-gray-600">Monitor and analyze Planning Center Online workflow performance</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">W</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Workflows</dt>
                  <dd className="text-lg font-medium text-gray-900">{workflowData.summary.totalWorkflows}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">C</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Cards</dt>
                  <dd className="text-lg font-medium text-gray-900">{workflowData.summary.totalCards.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">✓</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                  <dd className="text-lg font-medium text-gray-900">{workflowData.summary.completedCards.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">!</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Ready</dt>
                  <dd className="text-lg font-medium text-gray-900">{workflowData.summary.readyCards}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">⚠</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Overdue</dt>
                  <dd className="text-lg font-medium text-gray-900">{workflowData.summary.overdueCards}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overall Completion Rate */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Overall Completion Rate</h3>
        <div className="flex items-center">
          <div className="flex-1 bg-gray-200 rounded-full h-4">
            <div 
              className="bg-green-500 h-4 rounded-full transition-all duration-300"
              style={{ width: `${workflowData.summary.overallCompletionRate}%` }}
            ></div>
          </div>
          <span className="ml-4 text-lg font-medium text-gray-900">
            {workflowData.summary.overallCompletionRate}%
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Workflows Table */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Workflows by Volume</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workflow
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cards
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completion
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {workflowData.workflows.slice(0, 10).map((workflow) => (
                  <tr key={workflow.workflow_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{workflow.name}</div>
                      <div className="text-sm text-gray-500">Created {formatDate(workflow.created_at)}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>{workflow.total_cards_count} total</div>
                      <div className="text-gray-500">{workflow.completed_card_count} completed</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${workflow.completion_rate}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{workflow.completion_rate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Workflow Activity</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <div className="divide-y divide-gray-200">
              {workflowData.recentActivity.slice(0, 15).map((activity, index) => (
                <div key={`${activity.workflow_card_id}-${index}`} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {activity.workflow_name}
                      </div>
                      {activity.first_name && activity.last_name && (
                        <div className="text-sm text-gray-600">
                          {activity.first_name} {activity.last_name}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        {formatDateTime(activity.created_at)}
                      </div>
                    </div>
                    <div className="ml-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(activity.stage, activity.overdue)}`}>
                        {activity.overdue ? 'Overdue' : activity.stage}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Categories */}
      {workflowData.categories.length > 0 && (
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Workflow Categories</h3>
          </div>
          <div className="px-6 py-4">
            <div className="flex flex-wrap gap-2">
              {workflowData.categories.map((category) => (
                <span
                  key={category.workflow_category_id}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                >
                  {category.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
